import * as classnames from 'classnames'
import * as React from 'react'

import { customerMarketShopfrontApi } from '@envato/foundation-types'

import SearchLinkContainer from '../../../containers/searchLinkContainer'
import { IBlockMatchedComponent } from '../../../pages/category/category'
import { TabbedTableIcons } from '../../icons/categoryPage'
import { Tabs, Tab } from '../../../components/tabs'

import * as styles from './tabbedTableBlock.module.css'

const MAX_VERTICAL_ITEMS = 5

interface ITabbedNav {
  title: string
  icon: string
}

interface ITab extends ITabbedNav {
  content: customerMarketShopfrontApi.categoryPage.ISearchCta[]
}

interface ITabbedTableContent {
  tabs: ITab[]
  browseLinks: customerMarketShopfrontApi.categoryPage.ISearchCta[]
}

interface IProps extends IBlockMatchedComponent {
  data: ITabbedTableContent
}

const chevronIcon = (
  <span className={styles.tabbedTableChevronIcon}>
    {TabbedTableIcons.chevron_right}
  </span>
)

const renderLink = (className: string, appendix?: JSX.Element) => (
  link: customerMarketShopfrontApi.categoryPage.ISearchCta,
  linkIndex: number
) => (
  <SearchLinkContainer
    key={linkIndex}
    className={className}
    locationParams={link.urlSearchParams}
  >
    <span className={styles.linkInner}>
      {link.title}
      {appendix}
    </span>
  </SearchLinkContainer>
)

interface IViewHideButton {
  isCollapsed: boolean
  numberOfItems: number
  onClick: () => void
}

const ViewHideButton = ({
  isCollapsed,
  numberOfItems,
  onClick
}: IViewHideButton) => {
  if (numberOfItems <= MAX_VERTICAL_ITEMS) return null

  const canCollapseWhen2Columns = numberOfItems > MAX_VERTICAL_ITEMS * 2
  const classes = classnames(styles.viewHideButton, {
    [styles.canCollapseWhen2Columns]: canCollapseWhen2Columns
  })

  return (
    <button className={classes} onClick={onClick} aria-expanded={!isCollapsed}>
      {isCollapsed ? '+ View all' : '- Hide'}
    </button>
  )
}

const TabbedTableBlock = ({ data }: IProps) => {
  const [currentTab, setCurrentTab] = React.useState(0)
  const [isCollapsed, setIsCollapsed] = React.useState(true)
  let tabsNav: ITabbedNav[] = []
  let tabsContent: any[] = []

  data.tabs.forEach((tab: ITab) => {
    tabsNav.push({
      title: tab.title,
      icon: tab.icon
    })
    tabsContent.push(tab.content)
  })

  return (
    <section>
      <div className={styles.tabbedTableWrapper}>
        <Tabs value={currentTab} onChange={(tab: number) => setCurrentTab(tab)}>
          {tabsNav.map((item: ITabbedNav, navIndex: number) => (
            <Tab key={navIndex} value={navIndex}>
              <div className={styles.tabInner}>
                <span className={styles.tabbedNavLinkIcon}>
                  {TabbedTableIcons[item.icon]}
                </span>
                <span className={styles.tabbedNavLinkTitle}>{item.title}</span>
              </div>
            </Tab>
          ))}
        </Tabs>
        {tabsContent.map((item: [], tabIndex: number) => (
          <div
            key={tabIndex}
            className={classnames(styles.tabContent, {
              [styles.currentTab]: currentTab === tabIndex,
              [styles.collapsed]: isCollapsed
            })}
          >
            {item.map(renderLink(styles.tabbedContentLink, chevronIcon))}
          </div>
        ))}
        <ViewHideButton
          isCollapsed={isCollapsed}
          numberOfItems={tabsContent[currentTab].length}
          onClick={() => setIsCollapsed(!isCollapsed)}
        />
        {data.browseLinks.map(renderLink(styles.browseLink))}
      </div>
    </section>
  )
}

export default TabbedTableBlock
